import React, { useEffect, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { Avatar, Box, Link, Skeleton, Stack } from '@mui/material';

import { Swiper, SwiperSlide } from 'swiper/react';

import Axios from '../../../Assets/Axios/Axios';

import CustomNavigation from './CustomNavigation';
import { TypoWhite } from '../../../Components/Typo';

import { toCapitalize } from '../../../Helpers';
import getThumbnailImage from '../../../Helpers/getThumbnailImage';
import DefaultServiceImage from '../../../Assets/Images/DefaultService.svg';

import { useIsMobile } from '../../../Hooks/useIsMobile';

import { FeaturedProfessionalsQuery } from '../../../Server/Query';

const Slider2 = () => {
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const isMobile = useIsMobile();

  useEffect(() => {
    let active = true;

    Axios(FeaturedProfessionalsQuery)
      .then(({ data, errors }) => {
        if (!active) return;
        if (errors || !data?.featuredProfessionals) {
          setProfessionals([]);
          return;
        }

        setProfessionals(
          data.featuredProfessionals
            .filter(professional => professional.service)
            .map(({ id, avatar, firstName, lastName, service }) => ({
              id,
              avatar,
              title: [firstName, lastName].filter(Boolean).join(' '),
              role: service.name,
              src:
                service.bannerImage ??
                getThumbnailImage(service.name) ??
                DefaultServiceImage,
              serviceId: service.serviceId,
            }))
        );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  if (isLoading)
    return (
      <Stack direction='row' gap='2em' px={{ md: '31px' }} overflow='hidden'>
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
        <SkeletonCard flex={{ xs: '0 0 100%', md: '1 1 25%' }} />
      </Stack>
    );

  return (
    <Swiper
      slidesPerView={1}
      spaceBetween={32}
      breakpoints={{ 414: { slidesPerView: 2.3 }, 900: { slidesPerView: 4 } }}
      style={{
        paddingRight: isMobile ? undefined : '31px',
        paddingLeft: isMobile ? undefined : '31px',
      }}>
      {professionals.map(professional => (
        <SwiperSlide key={professional.id}>
          <Link component={RouterLink} to={`/professional/${professional.serviceId}`}>
            <Stack
              borderRadius='8px'
              width={1}
              sx={{
                aspectRatio: '1/1.3',
                background: `url(${professional.src})`,
                backgroundSize: 'cover',
              }}>
              <Stack direction='row' gap={1} pb={1} pl={1} alignItems='center' mt='auto'>
                <Avatar
                  src={professional.avatar}
                  sx={{
                    width: isMobile ? '28px' : undefined,
                    height: isMobile ? '28px' : undefined,
                  }}
                />
                <Box whiteSpace='nowrap' flex='1 1 0' minWidth={0}>
                  <TypoWhite noWrap variant='caption' fontWeight={500} display='block'>
                    {toCapitalize(professional.role)}
                  </TypoWhite>
                  <TypoWhite noWrap variant='caption' fontWeight={600} display='block'>
                    {professional.title}
                  </TypoWhite>
                </Box>
              </Stack>
            </Stack>
          </Link>
        </SwiperSlide>
      ))}
      {!isMobile && <CustomNavigation slidesCount={professionals.length} />}
    </Swiper>
  );
};

function SkeletonCard({ ...props }) {
  return (
    <Stack
      p={2}
      borderRadius={4}
      bgcolor='grey.200'
      boxSizing='border-box'
      sx={{ aspectRatio: '1/1.3' }}
      {...props}>
      <Stack mt='auto' direction='row' alignItems='center' gap={1}>
        <Skeleton
          sx={{
            aspectRatio: '1',
            height: 'auto',
            width: { xs: 28, md: 32 },
          }}
          variant='circular'
        />
        <Box flex='1 1 0'>
          <TypoWhite variant='caption'>
            <Skeleton />
          </TypoWhite>
          <TypoWhite variant='caption'>
            <Skeleton width='90%' />
          </TypoWhite>
        </Box>
      </Stack>
    </Stack>
  );
}

export default Slider2;
